import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { BaseTicketActionProps } from "./types";
import { SMSContextCard } from "../context-cards";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { Button, Combobox, ComboboxOption, Label, Textarea } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { formatName, formatPhone } from "@tellescope/utils";
import { MessageSquare } from "lucide-react";
import { useFromNumbers, usePhoneNumberLabel } from "../../../hooks/calling";

interface InboxSMSRecipientItemProps {
  enduser?: TellescopeModelFromName<"endusers">;
  value: string;
}

const InboxSMSRecipientItem = ({ enduser, value }: InboxSMSRecipientItemProps) => {
  return (
    <div className="flex gap-2 items-center">
      <div className="flex flex-col">
        {!!enduser && <p className="font-semibold">{formatName(enduser)}</p>}
        <p className={enduser ? "text-muted-foreground" : "font-semibold"}>{formatPhone(enduser?.phone ?? value)}</p>
      </div>
    </div>
  );
};

export interface SendSMSActionProps extends BaseTicketActionProps<"Send SMS"> {
  onSend: () => void;
}

export const SendSMSAction = ({ completedAt, info, onSend, enduserId }: SendSMSActionProps) => {
  const { t } = useTranslation("tickets");
  const [enduserSearch, setEnduserSearch] = useState("");

  const templateQuery = useTellescopeQuery({
    model: "templates",
    method: "getOne",
    request: info.templateId ?? "",
    enabled: !!info.templateId && !completedAt,
  });

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch
        ? {
            query: enduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: !completedAt,
  });

  const sendToEnduserMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "createOne",

    onSuccess: () => {
      onSend();
    },
  });

  const createEnduserMutation = useTellescopeMutation({
    model: "endusers",
    method: "createOne",
  });

  // Pre-load the initial recipient (from the enduserId prop) to seed the default "from" number.
  const initialEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId ?? "",
    enabled: !!enduserId && !completedAt,
  });

  const fromNumbers = useFromNumbers({ preferredNumber: initialEnduserQuery.data?.defaultFromPhone });
  const getPhoneLabel = usePhoneNumberLabel();

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: enduserId ?? "",
        required: true,
      },
      toNumber: {
        type: "select",
        initialValue: "",
        required: true,
      },
      fromNumber: {
        type: "select",
        initialValue: fromNumbers.defaultFromNumber,
        required: true,
      },
      message: {
        type: "textarea",
        initialValue: templateQuery.data?.message ?? "",
        required: true,
      },
    },
    validators: {
      message: (value) => (value.length <= 1200 ? undefined : t("Message cannot exceed 1200 characters")),
    },
    validateOn: "change",
    onSubmit: async (values) => {
      if (values.enduserId) {
        await sendToEnduserMutation.mutateAsync({
          message: values.message,
          enduserId: values.enduserId,
          enduserPhoneNumber: values.toNumber,
          phoneNumber: values.fromNumber,
        });
      } else {
        await createEnduserMutation.mutateAsync(
          {
            phone: values.toNumber,
          },
          {
            onSuccess: async (res) => {
              await sendToEnduserMutation.mutateAsync({
                message: values.message,
                enduserId: res.id,
                enduserPhoneNumber: values.toNumber,
                phoneNumber: values.fromNumber,
              });
            },
          }
        );
      }
    },
  });

  // Pre-load enduser if enduserId is provided
  const selectedEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: form.values.enduserId,
    enabled: !!form.values.enduserId && !completedAt,
  });

  if (completedAt && info.smsId) {
    return <SMSContextCard smsId={info.smsId} />;
  }

  return (
    <div className="space-y-4 border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center justify-between bg-gray-100 p-4 border-b border-b-gray-200">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MessageSquare className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Send SMS")}</h3>
        </div>
      </div>

      <form className="space-y-4 p-4" {...form.getFormProps()}>
        <div className="flex flex-row gap-4 w-full">
          <div className="flex-1 min-w-0 space-y-2">
            <Label htmlFor="from">{t("From")}</Label>
            <Select {...form.getCustomFieldProps("fromNumber")}>
              <Select.Trigger id="from" className="h-9 w-full">
                <Select.Value placeholder={t("Select phone number")} />
              </Select.Trigger>
              <Select.Content>
                {fromNumbers.options.map((number) => {
                  const label = getPhoneLabel(number);
                  return (
                    <Select.Item key={number} value={number}>
                      {label !== number ? `${label}: ${formatPhone(number)}` : formatPhone(number)}
                    </Select.Item>
                  );
                })}
              </Select.Content>
            </Select>
          </div>

          <div className="flex-1 min-w-0 space-y-2">
            <Label htmlFor="recipient">{t("To")}</Label>
            <Combobox
              value={{
                value: form.getFieldProps("toNumber").value,
                label: selectedEnduserQuery.data ? formatName(selectedEnduserQuery.data) : "",
                metadata: { enduserId: form.getFieldProps("enduserId").value },
              }}
              formatSelectedValue={({ value, label }) => (
                <p>
                  {label}
                  <span className="ml-1 text-muted-foreground">{formatPhone(value)}</span>
                </p>
              )}
              onChange={(newVal) => {
                if (typeof newVal?.metadata?.enduserId === "string") {
                  form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
                } else if (!newVal?.metadata?.enduserId) {
                  form.setFieldValue("enduserId", "");
                }
                form.setFieldValue("toNumber", newVal?.value ?? "");
              }}
              formatOption={(props) => (
                <InboxSMSRecipientItem
                  enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
                  value={props.option.value}
                />
              )}
              searchQuery={enduserSearch}
              setSearchQuery={setEnduserSearch}
              customFilter={(_, options) => options}
              options={
                endusersQuery.data?.reduce<ComboboxOption<string>[]>((acc, enduser) => {
                  if (!enduser.phone) {
                    return acc;
                  }

                  acc.push({
                    value: enduser.phone,
                    label: formatName(enduser),
                    metadata: {
                      enduserId: enduser.id,
                    },
                  });
                  return acc;
                }, []) ?? []
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">{t("Message")}</Label>
          <Textarea
            id="message"
            placeholder={t("Type your message...")}
            {...form.getFieldProps("message")}
            className="min-h-[120px] resize-none"
          />
          <p className="text-xs text-muted-foreground">
            {form.values.message.length}/1200 {t("characters")}
          </p>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={!form.isValid || form.isSubmitting}>
            {form.isSubmitting ? t("Sending...") : t("Send SMS")}
          </Button>
        </div>
      </form>
    </div>
  );
};
